import { useState } from 'react';
import { Card, Button, Loading } from '../common';
import { useAuthStore } from '../../store/authStore';
import { authAPI } from '../../api/auth';
import type { UserPreferences } from '../../types/user';

interface PrivacySettingsProps {
  onSuccess?: () => void;
}

export function PrivacySettings({ onSuccess }: PrivacySettingsProps) {
  const { user } = useAuthStore();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Initialize privacy settings with defaults
  const [privacySettings, setPrivacySettings] = useState({
    profileVisibility: user?.preferences?.privacy?.profileVisibility || 'private',
    shareReadings: user?.preferences?.privacy?.shareReadings || false,
    allowDataCollection: user?.preferences?.privacy?.allowDataCollection || false,
    showInSearchResults: user?.preferences?.privacy?.showInSearchResults || false,
    allowReadingSharing: user?.preferences?.privacy?.allowReadingSharing || false,
  });

  const handleSettingChange = (setting: string, value: boolean | string) => {
    setPrivacySettings(prev => ({
      ...prev,
      [setting]: value
    }));
  };

  const handleSave = async () => {
    try {
      setIsLoading(true);
      setError(null);
      setSuccess(null);

      const updatedPreferences: Partial<UserPreferences> = {
        ...user?.preferences,
        privacy: {
          ...user?.preferences?.privacy,
          profileVisibility: privacySettings.profileVisibility as 'public' | 'private',
          shareReadings: privacySettings.shareReadings,
          allowDataCollection: privacySettings.allowDataCollection,
          showInSearchResults: privacySettings.showInSearchResults,
          allowReadingSharing: privacySettings.allowReadingSharing,
        }
      };

      await authAPI.updateProfile({ preferences: updatedPreferences });

      setSuccess('Privacy settings updated successfully!');
      onSuccess?.();
    } catch (error: any) {
      console.error('Failed to update privacy settings:', error);
      setError(error.response?.data?.message || 'Failed to update privacy settings');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="p-4 bg-red-900/20 border border-red-500/30 rounded-lg">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      {success && (
        <div className="p-4 bg-green-900/20 border border-green-500/30 rounded-lg">
          <p className="text-green-400 text-sm">{success}</p>
        </div>
      )}

      <Card variant="elevated">
        <h3 className="text-xl font-semibold text-white mb-4">Profile Privacy</h3>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="text-white font-medium">Profile Visibility</label>
              <p className="text-gray-400 text-sm">Control who can see your profile information</p>
            </div>
            <select
              value={privacySettings.profileVisibility}
              onChange={(e) => handleSettingChange('profileVisibility', e.target.value)}
              className="bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="private">Private</option>
              <option value="public">Public</option>
            </select>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <label className="text-white font-medium">Show in Search Results</label>
              <p className="text-gray-400 text-sm">Allow your profile to appear in user searches</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={privacySettings.showInSearchResults}
                onChange={(e) => handleSettingChange('showInSearchResults', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
            </label>
          </div>
        </div>
      </Card>

      <Card variant="elevated">
        <h3 className="text-xl font-semibold text-white mb-4">Reading Privacy</h3>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="text-white font-medium">Allow Reading Sharing</label>
              <p className="text-gray-400 text-sm">Enable sharing of your readings with others</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={privacySettings.allowReadingSharing}
                onChange={(e) => handleSettingChange('allowReadingSharing', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
            </label>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <label className="text-white font-medium">Default Reading Privacy</label>
              <p className="text-gray-400 text-sm">Make new readings private by default</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={privacySettings.shareReadings}
                onChange={(e) => handleSettingChange('shareReadings', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
            </label>
          </div>
        </div>
      </Card>

      <Card variant="elevated">
        <h3 className="text-xl font-semibold text-white mb-4">Data Collection</h3>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="text-white font-medium">Allow Analytics</label>
              <p className="text-gray-400 text-sm">Help improve the app by sharing anonymous usage data</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={privacySettings.allowDataCollection}
                onChange={(e) => handleSettingChange('allowDataCollection', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
            </label>
          </div>
        </div>
      </Card>

      <div className="flex justify-end gap-3">
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={isLoading}
        >
          {isLoading ? <Loading size="sm" /> : 'Save Privacy Settings'}
        </Button>
      </div>
    </div>
  );
}